import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Settings, HelpCircle, BookOpen, Headphones, Upload, Camera } from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Separator } from "./ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export function UserAvatarMenu({ user, token, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      await axios.post(`${API}/users/avatar/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Avatar updated successfully");
      setAvatarDialogOpen(false);
      // Reload page to show new avatar
      window.location.reload();
    } catch (error) {
      toast.error("Failed to upload avatar");
    } finally {
      setUploading(false);
    }
  };

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || "U";
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 p-1 rounded-full hover:bg-muted transition-colors"
          data-testid="user-avatar-menu"
        >
          <Avatar className="h-9 w-9 border-2 border-primary/20">
            <AvatarImage src={user?.avatar_url} alt={user?.full_name} />
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-background border rounded-lg shadow-lg z-50">
            {/* User Info */}
            <div className="p-4 border-b">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={user?.avatar_url} alt={user?.full_name} />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{user?.full_name || "User"}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-3"
                onClick={() => {
                  setIsOpen(false);
                  setAvatarDialogOpen(true);
                }}
              >
                <Camera className="h-4 w-4 mr-2" />
                Change Avatar
              </Button>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <Link
                to="/profile"
                className="flex items-center gap-3 px-4 py-2 hover:bg-muted transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <User className="h-4 w-4 text-muted-foreground" />
                <span>My Profile</span>
              </Link>

              <Separator className="my-2" />

              <Link
                to="/faq"
                className="flex items-center gap-3 px-4 py-2 hover:bg-muted transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <span>FAQ</span>
              </Link>

              <Link
                to="/knowledge-base"
                className="flex items-center gap-3 px-4 py-2 hover:bg-muted transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span>Knowledge Base</span>
              </Link>

              <Link
                to="/support"
                className="flex items-center gap-3 px-4 py-2 hover:bg-muted transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <Headphones className="h-4 w-4 text-muted-foreground" />
                <span>Support</span>
              </Link>

              <Separator className="my-2" />

              <button
                onClick={() => {
                  setIsOpen(false);
                  onLogout();
                }}
                className="flex items-center gap-3 px-4 py-2 w-full text-left hover:bg-muted transition-colors text-red-600"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Avatar Upload Dialog */}
      <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Your Avatar</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <Avatar className="h-24 w-24">
              <AvatarImage src={user?.avatar_url} alt={user?.full_name} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-2xl">
                {getInitials()}
              </AvatarFallback>
            </Avatar>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarUpload}
              accept="image/*"
              className="hidden"
            />

            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Image
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Supported formats: JPG, PNG, GIF (max 5MB)
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UserAvatarMenu;
